import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { SavedView } from '../../lib/types'
import { Dialog } from '../../ds/Dialog'
import { Icon, type IconName } from '../../components/Icon'
import { moveHighlight, applicationSearchQuery, paletteRows, paletteEmptyCopy } from './paletteNav'
import { useSnippets } from '../snippets/SnippetsPage'
import { copyText, matchSnippet } from '../snippets/filter'

/** 片段命中在面板里最多列几条——它们排在服务端结果之后，不该把命令挤出视野。 */
const MAX_SNIPPET_HITS = 5

export interface SearchItem {
  /** snippet 行由前端用缓存的片段列表本地过滤得到，不来自 /search。 */
  kind: 'application' | 'company' | 'file' | 'snippet'
  id: number | string
  label: string
  hint: string
}

const KIND_ICON: Record<SearchItem['kind'], IconName> = {
  application: 'database',
  company: 'folder',
  file: 'file',
  snippet: 'snippet',
}

const KIND_LABEL: Record<SearchItem['kind'], string> = {
  application: '岗位',
  company: '公司',
  file: '文件',
  snippet: '片段 · 回车复制',
}

interface Cmd {
  key: string
  label: string
  sub: string
  icon: IconName
  run: () => void
}

const ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 8px',
  borderRadius: 4,
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'var(--type-ui)',
  color: 'var(--text)',
}

/**
 * ⌘K command palette: mixed 岗位 / 公司 / 文件 results from the cross-entity
 * search endpoint plus local commands (跳转 / 记一个岗位 / 已保存视图). 空输入
 * 时后端返回「最近打开」记录。Esc / backdrop 关闭。
 *
 * 全键盘可用：↑↓/Home/End 移动高亮，↵ 执行高亮行——行尾的 ↵ 标记不再只是
 * 装饰。输入法组字中的 Enter 交给 IME（isComposing），不会误触跳转。
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate()
  const [q, setQ] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // 高亮行索引，作用于 search 结果 + 命令 拼成的完整列表；查询变化后重置。
  const [highlight, setHighlight] = useState(0)

  const searchQ = useQuery({
    queryKey: ['search', 'palette', q],
    queryFn: () => api.get<{ items: SearchItem[] }>(`/api/v1/search?q=${encodeURIComponent(q)}&limit=20`),
    enabled: open,
    // 每个字换 queryKey 时不要把 items 清成 []，否则中文输入会闪「没有匹配「阿」」。
    placeholderData: (prev) => prev,
  })
  const viewsQ = useQuery({
    queryKey: ['views'],
    queryFn: () => api.get<{ items: SavedView[] }>('/api/v1/views'),
    enabled: open,
  })

  const snippetsQ = useSnippets(open)
  // 回车复制片段后在面板底部报一句结果，再关闭——否则用户不知道剪贴板里有没有东西。
  const [copyMsg, setCopyMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const navCmds: Cmd[] = useMemo(() => {
    const base: Cmd[] = [
      { key: 'today', label: '今日待办', sub: '首页 · 行动清单', icon: 'today', run: () => nav('/') },
      { key: 'db', label: '求职数据库', sub: '表格 / 看板 / 列表', icon: 'database', run: () => nav('/database') },
      { key: 'cal', label: '面试日历', sub: '周 / 月 / 议程', icon: 'calendar', run: () => nav('/calendar') },
      { key: 'stats', label: '统计分析', sub: '漏斗 · 桑基', icon: 'analytics', run: () => nav('/analytics') },
      { key: 'files', label: '文件库', sub: '私有附件', icon: 'files', run: () => nav('/files') },
      { key: 'snippets', label: '常用片段', sub: '实习经历 · 自我介绍', icon: 'snippet', run: () => nav('/snippets') },
      { key: 'settings', label: '设置', sub: '账号 · 字段', icon: 'settings', run: () => nav('/settings') },
      { key: 'new', label: '记一个岗位', sub: '新建申请', icon: 'plus', run: () => nav('/database?new=1') },
    ]
    const saved = (viewsQ.data?.items ?? [])
      .filter((v) => v.id >= 0)
      .slice(0, 6)
      .map<Cmd>((v) => ({
        key: `view-${v.id}`,
        label: `视图：${v.name}`,
        sub: `求职数据库 · ${v.layout === 'table' ? '表格' : v.layout === 'board' ? '看板' : '列表'}`,
        icon: 'rows',
        run: () => nav(`/database?view=${v.id}&layout=${v.layout}`),
      }))
    return [...base, ...saved]
  }, [nav, viewsQ.data])

  useEffect(() => {
    if (open) {
      setQ('')
      setCopyMsg(null)
      setHighlight(0)
      // Let the dialog mount before focusing its input.
      const t = setTimeout(() => inputRef.current?.focus(), 10)
      return () => clearTimeout(t)
    }
  }, [open])

  // 结果集随输入变化后，旧高亮可能指向已消失的行——回到首行。
  useEffect(() => {
    setHighlight(0)
  }, [q])

  const jump = (it: SearchItem) => {
    if (it.kind === 'snippet') {
      const sn = snippetsQ.data?.items.find((x) => x.id === it.id)
      if (!sn) return
      void copyText(sn.content).then((ok) => {
        setCopyMsg(ok ? { ok, text: `已复制「${sn.title}」` } : { ok, text: '浏览器拒绝了剪贴板，请到常用片段页手动复制' })
        if (ok) setTimeout(onClose, 700)
      })
      return
    }
    // 岗位行不再跳 /database/:id 去开抽屉：从数据库页上方打开面板时
    // /database → /database/:id 的路由变化不会重开抽屉（同一组件实例），
    // Enter 看起来毫无反应。改为把「公司 + 岗位」填进搜索框——与公司行
    // 一致的自动填写体验，多词 AND 语义会精确收敛到这条岗位。
    if (it.kind === 'application') {
      const query = applicationSearchQuery(it.label)
      nav(query ? `/database?q=${encodeURIComponent(query)}` : '/database')
    } else if (it.kind === 'company') {
      nav(`/database?q=${encodeURIComponent(it.label)}`)
    } else {
      nav('/files')
    }
    onClose()
  }

  // 中文命令关键字：输入「跳转 / 推进 / 记 / 新建 / 去 …」时只展示命令。
  const trimmed = q.trim().toLowerCase()
  const isCommandQuery = /^(跳转|推进|记|新建|打开|去|命令)/.test(trimmed)
  const filteredCmds = (trimmed === '' || isCommandQuery)
    ? navCmds.filter((c) => trimmed === '' || c.label.toLowerCase().includes(trimmed) || c.sub.toLowerCase().includes(trimmed))
    : []

  // 片段只在输入了非命令关键字时参与：空输入展示的是「最近打开」，片段不算。
  const snippetHits: SearchItem[] = useMemo(() => {
    if (trimmed === '' || isCommandQuery) return []
    return (snippetsQ.data?.items ?? [])
      .filter((sn) => matchSnippet(sn, q))
      .slice(0, MAX_SNIPPET_HITS)
      .map((sn) => ({
        kind: 'snippet' as const,
        id: sn.id,
        label: sn.title,
        hint: [sn.category, sn.content.split('\n')[0]].filter(Boolean).join(' · '),
      }))
  }, [snippetsQ.data, q, trimmed, isCommandQuery])
  const serverItems = searchQ.data?.items ?? []
  const items = useMemo(() => [...serverItems, ...snippetHits], [serverItems, snippetHits])

  // ↑↓ 键在“搜索结果 + 命令”的完整列表上移动；↵ 执行高亮行。
  const rows = useMemo(() => paletteRows(items, filteredCmds), [items, filteredCmds])

  const activate = (idx: number) => {
    const row = rows[idx]
    if (!row) return
    if (row.kind === 'cmd') {
      ;(row.data as Cmd).run()
      onClose()
    } else {
      jump(row.data)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 输入法组字中：Enter 在提交候选词，不是“执行高亮行”。
    if (e.nativeEvent.isComposing) return
    // Emacs/readline 风格：Ctrl+P 上一个、Ctrl+N 下一个（与 ↑↓ 等价）。
    // preventDefault 顺带拦下部分平台的 Ctrl+P 打印对话框。
    let navKey = e.key
    if (e.ctrlKey) {
      const lower = e.key.toLowerCase()
      if (lower === 'p') navKey = 'ArrowUp'
      else if (lower === 'n') navKey = 'ArrowDown'
    }
    const next = moveHighlight(highlight, rows.length, navKey)
    if (next !== null) {
      e.preventDefault()
      setHighlight(next)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      activate(highlight)
    }
  }

  // 高亮行滚进可视区（结果多时 ↓ 到列表底部不能盲飞）。
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-highlight="1"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [highlight])

  return (
    <Dialog open={open} onClose={onClose} title="快速搜索" width={560} description="搜岗位、公司、备注与文件，或直接跳转">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
        <Icon name="search" size={16} color="var(--neutral-500)" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="搜岗位、公司、跳转、推进阶段…"
          aria-label="命令面板搜索"
          aria-describedby="cmd-kbd-nav"
          style={{
            flex: 1,
            border: 0,
            outline: 'none',
            background: 'transparent',
            font: 'var(--type-ui)',
            fontSize: 'var(--text-15)',
            color: 'var(--text)',
          }}
        />
        <span className="kbd" aria-hidden>
          ⌘K
        </span>
      </div>
      <span id="cmd-kbd-nav" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        ↑↓ 或 Ctrl+P / Ctrl+N 移动高亮，回车执行高亮行，Esc 关闭
      </span>

      <div ref={listRef} role="listbox" aria-label="搜索结果与命令" style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 380, overflowY: 'auto' }}>
        {items.map((it, i) => {
          const on = highlight === i
          return (
            <button
              key={`${it.kind}-${it.id}`}
              type="button"
              className="cmd-row"
              role="option"
              aria-selected={on}
              data-highlight={on ? '1' : '0'}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => jump(it)}
              style={{ ...ROW_STYLE, background: on ? 'var(--accent-soft, color-mix(in srgb, var(--accent) 12%, transparent))' : undefined }}
            >
              <span
                aria-hidden
                style={{
                  width: 22,
                  height: 22,
                  flex: '0 0 auto',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'var(--surface-thin)',
                  border: '1px solid var(--border)',
                  color: 'var(--neutral-600)',
                }}
              >
                <Icon name={KIND_ICON[it.kind]} size={13} />
              </span>
              <span className="grow" style={{ minWidth: 0 }}>
                <span className="ellipsis" style={{ display: 'block', fontSize: 14 }}>
                  {it.label}
                </span>
                <span className="ellipsis" style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>
                  {KIND_LABEL[it.kind]}
                  {it.hint ? ` · ${it.hint}` : ''}
                </span>
              </span>
              <span style={{ fontSize: 10, color: 'var(--neutral-500)', flex: '0 0 auto' }}>↵</span>
            </button>
          )
        })}

        {items.length > 0 && filteredCmds.length > 0 && (
          <div style={{ margin: '6px 8px', borderTop: '1px dashed var(--border)' }} />
        )}

        {filteredCmds.map((c, j) => {
          const i = items.length + j
          const on = highlight === i
          return (
            <button
              key={c.key}
              type="button"
              className="cmd-row"
              role="option"
              aria-selected={on}
              data-highlight={on ? '1' : '0'}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => {
                c.run()
                onClose()
              }}
              style={{ ...ROW_STYLE, background: on ? 'var(--accent-soft, color-mix(in srgb, var(--accent) 12%, transparent))' : undefined }}
            >
              <span
                aria-hidden
                style={{ width: 22, flex: '0 0 auto', display: 'grid', placeItems: 'center', color: 'var(--neutral-600)' }}
              >
                <Icon name={c.icon} size={14} />
              </span>
              <span className="grow" style={{ minWidth: 0 }}>
                <span className="ellipsis" style={{ display: 'block', fontSize: 14 }}>
                  {c.label}
                </span>
                <span className="ellipsis" style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>
                  {c.sub}
                </span>
              </span>
              <span style={{ fontSize: 10, color: 'var(--neutral-500)', flex: '0 0 auto' }}>↵</span>
            </button>
          )
        })}

        {items.length === 0 && filteredCmds.length === 0 && (
          <div style={{ padding: '24px 8px', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
            {paletteEmptyCopy(q, searchQ.isFetching)}
          </div>
        )}
      </div>
      {copyMsg && (
        <div role="status" style={{ marginTop: 8, fontSize: 12, color: copyMsg.ok ? 'var(--text-muted)' : 'var(--danger)' }}>
          {copyMsg.text}
        </div>
      )}
    </Dialog>
  )
}
